import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Gift, Download, FileText, LogIn, Loader2, CheckCircle2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { API_CONFIG } from '../../config/api.config'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import LoginModal from '../../components/auth/LoginModal'

const base = API_CONFIG.python.apiUrl
const token = () => localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''

interface GiftInfo { slug: string; title: string; description?: string; article_url?: string }

const GiftLandingPage: React.FC = () => {
  const { slug } = useParams()
  const [sp] = useSearchParams()
  const fb = sp.get('fb') || ''
  const { isAuthenticated, isLoading } = useAuth()

  const [gift, setGift] = useState<GiftInfo | null | undefined>(undefined)
  const [driver, setDriver] = useState<string>('')
  const [claiming, setClaiming] = useState(false)
  const [err, setErr] = useState('')
  const [showLogin, setShowLogin] = useState(false)

  // Thông tin quà (công khai — chưa lộ link tải)
  useEffect(() => {
    if (!slug) return
    fetch(`${base}/fb/gift/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setGift(d))
      .catch(() => setGift(null))
  }, [slug])

  // Đã đăng nhập → tự nhận quà (ghi lead + trả link tải)
  useEffect(() => {
    if (!isAuthenticated || !slug || driver || claiming) return
    setClaiming(true); setErr('')
    fetch(`${base}/fb/claim`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, fb }),
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(d?.detail || 'Không nhận được quà')
        setDriver(d.driver_url || '')
        if (d.article_url && gift && !gift.article_url) setGift({ ...gift, article_url: d.article_url })
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setClaiming(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, slug])

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      <Navigation />
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg rounded-3xl border border-blue-100 bg-white p-7 shadow-xl">
          {gift === undefined ? (
            <div className="py-10 text-center text-gray-400"><Loader2 className="mx-auto animate-spin" /> Đang tải…</div>
          ) : !gift ? (
            <div className="py-10 text-center">
              <p className="text-lg font-black text-gray-800">Không tìm thấy quà tặng</p>
              <p className="mt-1 text-sm text-gray-500">Liên kết có thể đã hết hạn hoặc sai. Kiểm tra lại tin nhắn nhé.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-100 text-emerald-600"><Gift size={28} /></span>
                <div>
                  <p className="text-[11px] font-black uppercase tracking-wide text-emerald-600">Quà tặng cho giáo viên</p>
                  <h1 className="text-xl font-black text-gray-900">{gift.title}</h1>
                </div>
              </div>
              {gift.description && <p className="mt-4 text-sm leading-relaxed text-gray-600 whitespace-pre-line">{gift.description}</p>}
              {gift.article_url && (
                <a href={gift.article_url} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:underline">
                  <FileText size={15} /> Đọc bài viết hướng dẫn
                </a>
              )}

              <div className="mt-6">
                {!isAuthenticated ? (
                  <>
                    <button
                      onClick={() => setShowLogin(true)}
                      disabled={isLoading}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 text-base font-black text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      <LogIn size={18} /> Đăng nhập bằng Google để nhận
                    </button>
                    <p className="mt-2 text-center text-xs text-gray-400">Chỉ cần Gmail — đăng nhập một lần là tải được.</p>
                  </>
                ) : claiming ? (
                  <div className="py-4 text-center text-gray-500"><Loader2 className="mx-auto animate-spin" /> Đang chuẩn bị quà…</div>
                ) : driver ? (
                  <>
                    <div className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-emerald-50 py-2 text-sm font-bold text-emerald-700">
                      <CheckCircle2 size={16} /> Đã sẵn sàng cho bạn!
                    </div>
                    <a
                      href={driver}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3.5 text-base font-black text-white hover:bg-emerald-700"
                    >
                      <Download size={18} /> Tải xuống ngay
                    </a>
                  </>
                ) : (
                  <div className="rounded-xl bg-amber-50 p-3 text-center text-sm font-semibold text-amber-700">
                    {err || 'Quà đang được cập nhật, thầy cô quay lại sau ít phút nhé.'}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </div>
  )
}

export default GiftLandingPage
